/**
 * Swept-circle continuous collision detection, in the xz plane.
 *
 * Every query is "a circle of radius r at p moving by displacement d this
 * tick" and returns the earliest time of impact t ∈ [0, 1] plus the contact
 * normal, or null. Callers use t to advance to the contact and the normal to
 * reflect/slide. Pure, allocation-light, deterministic.
 */

export interface Hit {
  /** Fraction of the displacement at which contact occurs, 0..1. */
  t: number
  /** Unit normal pointing away from the obstacle, toward the mover. */
  nx: number
  nz: number
}

const EPS = 1e-9

/** Moving circle vs static circle (Minkowski: point vs circle of r + cr). */
export function sweepCircleCircle(
  px: number, pz: number, dx: number, dz: number, r: number,
  cx: number, cz: number, cr: number,
): Hit | null {
  const R = r + cr
  const fx = px - cx
  const fz = pz - cz
  const a = dx * dx + dz * dz
  const b = 2 * (fx * dx + fz * dz)
  const c = fx * fx + fz * fz - R * R
  if (c < 0) {
    // Already overlapping: report t=0 with the separating normal.
    const l = Math.hypot(fx, fz)
    return l > EPS ? { t: 0, nx: fx / l, nz: fz / l } : { t: 0, nx: 1, nz: 0 }
  }
  if (a < EPS) return null // not moving
  if (b >= 0) return null // moving away
  const disc = b * b - 4 * a * c
  if (disc < 0) return null
  const t = (-b - Math.sqrt(disc)) / (2 * a)
  if (t < 0 || t > 1) return null
  const hx = px + dx * t - cx
  const hz = pz + dz * t - cz
  const l = Math.hypot(hx, hz) || 1
  return { t, nx: hx / l, nz: hz / l }
}

/** Moving circle vs static line segment (a→b), including its end caps. */
export function sweepCircleSegment(
  px: number, pz: number, dx: number, dz: number, r: number,
  ax: number, az: number, bx: number, bz: number,
): Hit | null {
  const ex = bx - ax
  const ez = bz - az
  const el = Math.hypot(ex, ez)
  if (el < EPS) return sweepCircleCircle(px, pz, dx, dz, r, ax, az, 0)
  // Segment normal (choose the side the circle starts on).
  let nx = -ez / el
  let nz = ex / el
  const side = (px - ax) * nx + (pz - az) * nz
  if (side < 0) { nx = -nx; nz = -nz }
  const dist0 = Math.abs(side)
  const vn = dx * nx + dz * nz // velocity toward the plane (negative = approaching)

  let best: Hit | null = null
  if (dist0 < r) {
    // Already within r of the infinite line — check we're within the segment span.
    const proj = ((px - ax) * ex + (pz - az) * ez) / el
    if (proj >= 0 && proj <= el) best = { t: 0, nx, nz }
  } else if (vn < -EPS) {
    const t = (dist0 - r) / -vn
    if (t >= 0 && t <= 1) {
      const hx = px + dx * t
      const hz = pz + dz * t
      const proj = ((hx - ax) * ex + (hz - az) * ez) / el
      if (proj >= 0 && proj <= el) best = { t, nx, nz }
    }
  }
  // End caps.
  for (const [cx, cz] of [[ax, az], [bx, bz]] as const) {
    const h = sweepCircleCircle(px, pz, dx, dz, r, cx, cz, 0)
    if (h && (!best || h.t < best.t)) best = h
  }
  return best
}

/** Reflect a velocity about a unit normal, with optional restitution. */
export function reflect(vx: number, vz: number, nx: number, nz: number, restitution = 1): [number, number] {
  const d = vx * nx + vz * nz
  return [(vx - 2 * d * nx) * restitution, (vz - 2 * d * nz) * restitution]
}

/** Static overlap test — circle vs circle. */
export function circlesOverlap(ax: number, az: number, ar: number, bx: number, bz: number, br: number): boolean {
  const dx = ax - bx
  const dz = az - bz
  const R = ar + br
  return dx * dx + dz * dz < R * R
}

/** Closest approach between a moving circle and a static circle over the
 *  displacement — true if they touch at any point during the sweep. Used for
 *  hit detection so a fast disc can't tunnel through a player. */
export function sweptOverlap(
  px: number, pz: number, dx: number, dz: number, r: number,
  cx: number, cz: number, cr: number,
): boolean {
  return sweepCircleCircle(px, pz, dx, dz, r, cx, cz, cr) !== null
}

/** Bearing test for the shield arc (04 §3): is the direction player→point
 *  within ±halfDeg of `aim`? */
export function withinArc(px: number, pz: number, aimX: number, aimZ: number, qx: number, qz: number, halfDeg: number): boolean {
  const dx = qx - px
  const dz = qz - pz
  const l = Math.hypot(dx, dz)
  if (l < EPS) return true
  const cos = (dx * aimX + dz * aimZ) / l
  return cos >= Math.cos((halfDeg * Math.PI) / 180) - 1e-9
}

/** Distance from a point to a segment. */
export function pointSegmentDistance(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const ex = bx - ax
  const ez = bz - az
  const el2 = ex * ex + ez * ez
  const t = el2 < EPS ? 0 : Math.max(0, Math.min(1, ((px - ax) * ex + (pz - az) * ez) / el2))
  return Math.hypot(px - (ax + ex * t), pz - (az + ez * t))
}
